import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface SkippedPageRow {
  raw_bundle_id: string;
  file_key: string;
  page_number: number;
}

/** Page numbers the summary did not read, by raw bundle id and then file key. */
export type SkippedPagesByBundle = ReadonlyMap<string, ReadonlyMap<string, readonly number[]>>;

export function groupSkippedPages(rows: readonly SkippedPageRow[]): SkippedPagesByBundle {
  const byBundle = new Map<string, Map<string, number[]>>();
  for (const row of rows) {
    const byFile = byBundle.get(row.raw_bundle_id) ?? new Map<string, number[]>();
    byFile.set(
      row.file_key,
      [...(byFile.get(row.file_key) ?? []), row.page_number].sort((a, b) => a - b),
    );
    byBundle.set(row.raw_bundle_id, byFile);
  }
  return byBundle;
}

async function fetchSkippedPagesForEncounter(encounterId: string): Promise<SkippedPagesByBundle> {
  const { data, error } = await supabase
    .from("codeable_summary_skipped_pages")
    .select("raw_bundle_id, file_key, page_number")
    .eq("encounter_id", encounterId);

  if (error) throw error;
  return groupSkippedPages(data);
}

export function useSkippedPagesForEncounter(encounterId: string | null) {
  return useQuery({
    queryKey: ["raw-files-viewer-skipped-pages", encounterId],
    queryFn: () => fetchSkippedPagesForEncounter(encounterId!),
    enabled: !!encounterId,
  });
}
